// Adım 3 — Hesaplama ve Önizleme (M16 T11; mockup 06, Eng 11 overlay dry-run). `POST /preview`
// DB'ye yazmaz: diff özeti kartları, çakışma kuralı radyosu, yazılacak hücreler (25'lik sayfa),
// atlanan satırlar + uyarılar + eşlenmemiş işletme, türetilmiş göstergelerin yeniden hesabı
// (önce = mevcut grid, sonra = overlay). "AI Kontrolü" paneli (T15) adımın altında.
// güvenlik/UX taraması 2026-10-08 (600 satır sınırı): "Yazılacak hücreler" ve "Türetilmiş" tabloları
// `ImportPreviewTables.tsx`'te (davranış birebir).

import { useEffect, useMemo, useState } from "react";
import {
  formatKpiNumber,
  type KpiImportColumn,
  type KpiMappingDefaults,
  type KpiMappingRow,
  type KpiResolvedGrid,
} from "@mahir/shared";
import {
  INTERNAL_IMPORT_API,
  KpiImportRequestError,
  type ImportApi,
  type KpiConflictPolicy,
  type KpiImportCommitResult,
  type KpiImportJobDto,
  type KpiImportPreview,
} from "../../../api/kpi-import";
import { savableMapping } from "../../../lib/kpi-import-breakdown";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import {
  accessErrorRowLabels,
  cellsToWrite,
  commitUnitNote,
  countUnitStates,
  derivedPreviewRows,
  formatIssues,
  paginate,
  summarizePlan,
} from "../../../lib/kpi-import-wizard";
import { portalErrorMessage } from "../../../lib/portal-kpi-ui";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { ErrorState, LoadingState } from "../../common/StateSurface";
import { AiCheckPanel } from "../AiCheckPanel";
import { amberBox, btnGhost, btnPrimary, errorBox } from "../kpi-form-styles";
import {
  IncompatibleRowItem,
  UnitChangeDialog,
  UnitCounterBar,
  type UnitFilter,
} from "./ImportUnitControls";
import { ImportPreviewBreakdownSection } from "./ImportPreviewBreakdown";
import { ImportPreviewCellsSection, ImportPreviewDerivedSection } from "./ImportPreviewTables";

export interface ImportPreviewStepProps {
  job: KpiImportJobDto;
  mapping: KpiMappingRow[];
  columns: KpiImportColumn[][];
  /** B-02/B-03 — plana giren yıl/işletme varsayılanları */
  defaults: KpiMappingDefaults;
  preview: KpiImportPreview | null;
  onPreview: (p: KpiImportPreview) => void;
  policy: KpiConflictPolicy;
  onPolicy: (p: KpiConflictPolicy) => void;
  onBack: () => void;
  onCommitted: (res: KpiImportCommitResult, preview: KpiImportPreview) => void;
  /** T19 — API tabanı (iç / portal) */
  api?: ImportApi;
  /** T19 — müşteri modu: AI Kontrolü paneli render edilmez; 400/403 anlaşılır metin */
  isClient?: boolean;
  /** türetilmiş "önce" değerleri için mevcut grid (iç: kpi-store, portal: portal store) */
  currentGrid: KpiResolvedGrid | null;
  /** DR7 — iç sihirbaz: eşleştirmeyi günceller (önizleme yeniden hesaplanır). Portalda verilmez. */
  onMappingChange?: (next: KpiMappingRow[]) => void;
}

/** TODO-KPI-37 — belirsiz hücrede iki okuma da tam haneyle (varsayılan 2 hane kırpardı). */
function exactNumber(v: number): string {
  return formatKpiNumber(v, { decimals: Number.isInteger(v) ? 0 : 3 });
}

const card: React.CSSProperties = {
  border: "1px solid var(--color-border)",
  borderRadius: 12,
  padding: "12px 16px",
  background: "var(--color-surface)",
  minWidth: 180,
  flex: "1 1 180px",
};
const kicker: React.CSSProperties = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  color: "var(--color-text-3)",
};
const bigNum: React.CSSProperties = {
  fontSize: "1.6rem",
  fontWeight: 700,
  color: "var(--color-primary)",
  lineHeight: 1.2,
};

export function ImportPreviewStep(props: ImportPreviewStepProps): JSX.Element {
  const {
    job,
    mapping,
    defaults,
    preview,
    onPreview,
    policy,
    onPolicy,
    onBack,
    onCommitted,
    currentGrid,
  } = props;
  const api = props.api ?? INTERNAL_IMPORT_API;
  const isClient = props.isClient ?? false;
  const p = KPI_IMPORT_COPY.preview;
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [committing, setCommitting] = useState(false);
  const [cellPage, setCellPage] = useState(0);
  const [derivedPage, setDerivedPage] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [unitFilter, setUnitFilter] = useState<UnitFilter>("all");
  const [unitDialog, setUnitDialog] = useState<string | null>(null);
  const [skipConfirm, setSkipConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (preview) return;
    let alive = true;
    setError(null);
    setIssues([]);
    api
      .preview(job.id, savableMapping(mapping), defaults)
      .then((res) => {
        if (alive) onPreview(res);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (err instanceof KpiImportRequestError && err.issues.length > 0)
          setIssues(formatIssues(err.issues));
        setError(err instanceof Error ? err.message : KPI_IMPORT_COPY.genericError);
      });
    return () => {
      alive = false;
    };
  }, [job.id, mapping, defaults, preview, onPreview, reloadKey, api]);

  const summary = useMemo(() => (preview ? summarizePlan(preview.plan) : null), [preview]);
  // Kullanıcının "Atla" dediği satırlar plan.skipped'a girmez (sunucu sessiz geçer) — karta eklenir.
  const userSkipped = useMemo(() => mapping.filter((r) => r.action === "skip").length, [mapping]);
  const derived = useMemo(
    () =>
      preview
        ? derivedPreviewRows(
            preview.recalculated,
            currentGrid,
            new Set(
              preview.plan.cells.filter((c) => c.outcome !== "skip_derived").map((c) => c.code),
            ),
          )
        : [],
    [preview, currentGrid],
  );
  const entityName = useMemo(
    () => new Map((preview?.recalculated.entities ?? []).map((e) => [e.id, e.name])),
    [preview],
  );
  const labelByCode = useMemo(
    () => new Map((preview?.recalculated.indicators ?? []).map((i) => [i.code, i.label])),
    [preview],
  );
  const rowLabel = useMemo(() => new Map(mapping.map((r) => [r.rowId, r.rowLabel])), [mapping]);
  const unitCounts = useMemo(
    () => (preview ? countUnitStates(preview.plan) : { converted: 0, incompatible: 0, unknown: 0 }),
    [preview],
  );
  const writable = useMemo(
    () =>
      preview
        ? preview.plan.cells.filter(
            (c) =>
              c.outcome !== "skip_derived" &&
              c.outcome !== "unchanged" &&
              (unitFilter === "all" ||
                (unitFilter === "converted" && c.converted !== undefined) ||
                (unitFilter === "unknown" && c.unitNote !== undefined)),
          )
        : [],
    [preview, unitFilter],
  );
  const mappingByRow = useMemo(() => new Map(mapping.map((r) => [r.rowId, r])), [mapping]);

  function skipRow(rowId: string): void {
    if (!props.onMappingChange) return;
    const next = mapping.map((r) => (r.rowId === rowId ? { ...r, action: "skip" as const } : r));
    props.onMappingChange(next);
  }
  function afterUnitApplied(): void {
    // eşleştirme aynı kalır; yalnız önizleme yeniden hesaplanır (taslak zaten sunucuda)
    props.onMappingChange?.([...mapping]);
  }
  const cells = paginate(writable, cellPage);
  const derivedPaged = paginate(derived, derivedPage);
  const toWrite = summary ? cellsToWrite(summary, policy) : 0;
  const accessSkipped = useMemo(
    () => (preview ? preview.plan.skipped.filter((k) => k.access !== undefined) : []),
    [preview],
  );
  // Aktar düğmesi son ek: birim yüzünden atlanacak satırlar + çevrilmeden yazılacak hücreler
  const unitNote = commitUnitNote(unitCounts, KPI_IMPORT_COPY.unit);
  const blocked =
    !preview ||
    preview.plan.unresolvedEntities.length > 0 ||
    preview.plan.missing.length > 0 ||
    toWrite === 0;

  async function commit(): Promise<void> {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    setIssues([]);
    try {
      const res = await api.commit(job.id, savableMapping(mapping), policy, defaults);
      onCommitted(res, preview);
    } catch (err) {
      if (err instanceof KpiImportRequestError) {
        if (err.code === "KPI_JOB_ALREADY_COMMITTED") setError(p.alreadyCommitted);
        else if (
          isClient &&
          (err.code === "KPI_CLIENT_FORBIDDEN_ACTION" || err.code === "KPI_CLIENT_FORBIDDEN")
        ) {
          // C3 — iş `ready` kalır, hiçbir şey yazılmadı; satırları adlandır (P1-2)
          const labels = accessErrorRowLabels(err.details);
          setError(
            labels.length > 0
              ? KPI_IMPORT_COPY.client.forbiddenRows(labels)
              : portalErrorMessage(err.code, err.message),
          );
        } else {
          setIssues(formatIssues(err.issues));
          setError(`${p.commitFailed} ${err.message}`);
        }
      } else setError(`${p.commitFailed} ${err instanceof Error ? err.message : ""}`.trim());
    } finally {
      setCommitting(false);
    }
  }

  if (!preview) {
    if (error)
      return (
        <ErrorState
          title={p.failed}
          message={[error, ...issues].join(" · ")}
          onRetry={() => setReloadKey((k) => k + 1)}
        />
      );
    return <LoadingState label={p.loading} />;
  }
  const s = summary!;
  const plan = preview.plan;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3 flex-wrap">
        <div style={card}>
          <div style={kicker}>{p.cards.create.title}</div>
          <div style={bigNum}>{s.create}</div>
          <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {p.cards.create.hint}
          </div>
        </div>
        <div style={card}>
          <div style={kicker}>{p.cards.update.title}</div>
          <div style={bigNum}>{s.update}</div>
          <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {p.cards.update.hint}
          </div>
        </div>
        <div style={card}>
          <div style={kicker}>{p.cards.conflict.title}</div>
          <div style={bigNum}>{s.conflict}</div>
          <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {p.cards.conflict.hint}
          </div>
        </div>
        <div style={card}>
          <div style={kicker}>{p.cards.skipped.title}</div>
          <div style={bigNum}>{s.skippedRows + userSkipped}</div>
          <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {p.cards.skipped.hint(
              s.skippedDerivedRows,
              s.skippedRows - s.skippedDerivedRows + userSkipped,
            )}
          </div>
        </div>
        <div style={card}>
          <div style={kicker}>{p.cards.derived.title}</div>
          <div style={bigNum}>{derived.filter((d) => d.changed).length}</div>
          <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
            {p.cards.derived.hint}
          </div>
        </div>
      </div>

      {plan.unresolvedEntities.length > 0 ? (
        <div role="alert" style={amberBox}>
          <b>{p.unresolvedTitle}:</b> {plan.unresolvedEntities.join(", ")} — {p.unresolvedBody}
        </div>
      ) : null}
      {plan.missing.includes("year") ? (
        <div role="alert" style={amberBox}>
          {(plan.missingYearSheets ?? []).length > 0 && job.grid
            ? p.missingYearSheets(
                plan.missingYearSheets.map((i) => job.grid?.sheets[i]?.name ?? `#${String(i + 1)}`),
              )
            : p.missingYear}
        </div>
      ) : null}
      {(plan.skippedSheets ?? []).length > 0 && job.grid ? (
        <div
          className="text-sm"
          style={{ color: "var(--color-text-2)" }}
          data-testid="kpi-import-skipped-sheets"
        >
          {p.skippedSheets(
            plan.skippedSheets.map((i) => job.grid?.sheets[i]?.name ?? `#${String(i + 1)}`),
          )}
        </div>
      ) : null}
      {plan.missing.includes("entity") ? (
        <div role="alert" style={amberBox}>
          {p.missingEntity}
        </div>
      ) : null}
      {plan.newIndicatorCodes.length > 0 ? (
        <div style={amberBox}>{p.newIndicators(plan.newIndicatorCodes)}</div>
      ) : null}

      <UnitCounterBar counts={unitCounts} filter={unitFilter} onFilter={setUnitFilter} />

      <div className="flex gap-4 items-start flex-wrap">
        <div className="flex flex-col gap-4" style={{ flex: "1 1 640px", minWidth: 0 }}>
          <ImportPreviewCellsSection
            writableCount={writable.length}
            cells={cells}
            labelByCode={labelByCode}
            entityName={entityName}
            policy={policy}
            onPrev={() => setCellPage((x) => x - 1)}
            onNext={() => setCellPage((x) => x + 1)}
          />

          <ImportPreviewBreakdownSection
            plan={plan}
            recalculated={preview.recalculated}
            mapping={mapping}
            grid={job.grid}
            columns={props.columns}
          />

          <ImportPreviewDerivedSection
            derivedCount={derived.length}
            derivedPaged={derivedPaged}
            onPrev={() => setDerivedPage((x) => x - 1)}
            onNext={() => setDerivedPage((x) => x + 1)}
          />
        </div>

        <aside className="flex flex-col gap-4" style={{ flex: "0 1 340px", minWidth: 280 }}>
          <fieldset style={{ ...card, margin: 0 }}>
            <legend className="font-semibold px-1">{p.policyTitle}</legend>
            {(["overwrite", "keep_existing"] as const).map((v) => (
              <label
                key={v}
                className="flex items-start gap-2 text-sm py-2"
                style={{ cursor: "pointer" }}
              >
                <input
                  type="radio"
                  name="kpi-import-policy"
                  value={v}
                  checked={policy === v}
                  onChange={() => onPolicy(v)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <b>{p.policy[v].label}</b>
                  <div style={{ color: "var(--color-text-3)" }}>
                    {v === "overwrite"
                      ? p.policy.overwrite.hint(s.update + s.conflict)
                      : p.policy.keep_existing.hint}
                  </div>
                </span>
              </label>
            ))}
          </fieldset>

          {plan.warnings.length > 0 ? (
            <div style={card}>
              <div className="font-semibold mb-1">⚠ {p.warningsTitle(plan.warnings.length)}</div>
              <ul
                className="flex flex-col gap-1 text-sm"
                style={{ paddingLeft: 16, color: "var(--color-text-2)" }}
              >
                {plan.warnings.map((w, i) => (
                  <li key={i.toString()}>{w}</li>
                ))}
              </ul>
              {/* P2-3 — aynı hedefe düşen hücrelerin tek tek listesi: katlanabilir (631 satır değil tek özet) */}
              {(plan.dedupeDetails ?? []).length > 0 ? (
                <details className="mt-2 text-sm" data-testid="kpi-import-dedupe-details">
                  <summary style={{ cursor: "pointer", color: "var(--color-primary)" }}>
                    {p.dedupeDetails(plan.dedupeDetails.length)}
                  </summary>
                  <ul
                    className="flex flex-col gap-1 mt-1"
                    style={{
                      paddingLeft: 16,
                      color: "var(--color-text-3)",
                      maxHeight: 240,
                      overflowY: "auto",
                    }}
                  >
                    {plan.dedupeDetails.map((w, i) => (
                      <li key={i.toString()}>{w}</li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}

          {(plan.localeWarnings ?? []).length > 0 ? (
            <div style={amberBox} data-testid="kpi-import-locale-ambiguous" role="status">
              <div className="font-semibold mb-1">
                ⚠ {p.localeAmbiguous((plan.localeWarnings ?? []).length)}
              </div>
              <div className="text-sm mb-1">{p.localeAmbiguousHint}</div>
              <ul
                className="flex flex-col gap-1 text-sm"
                style={{ paddingLeft: 16, maxHeight: 200, overflowY: "auto" }}
              >
                {(plan.localeWarnings ?? []).map((w) => (
                  <li key={`${w.sheet}|${w.rowId}|${w.col}`}>
                    {p.localeAmbiguousCell(
                      w.sheet,
                      `${w.col}${w.rowId.split(":")[1] ?? ""}`,
                      w.raw,
                      exactNumber(w.value),
                      // virgüllü yazım ondalık okundu → diğer okuma binlik (×1000); noktalı tersi
                      exactNumber(
                        w.raw.includes(",") ? Math.round(w.value * 1000) : w.value / 1000,
                      ),
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {isClient && accessSkipped.length > 0 ? (
            <div style={card} data-testid="kpi-import-access-skipped">
              <div className="font-semibold mb-1">
                {KPI_IMPORT_COPY.client.accessSkippedTitle(accessSkipped.length)}
              </div>
              <ul
                className="flex flex-col gap-1 text-sm"
                style={{ paddingLeft: 16, color: "var(--color-text-2)" }}
              >
                {accessSkipped.map((k) => (
                  <li key={k.rowId}>
                    <b>{rowLabel.get(k.rowId) ?? k.rowId}</b> — {k.reason}
                  </li>
                ))}
              </ul>
              <div className="text-sm mt-1" style={{ color: "var(--color-text-3)" }}>
                {KPI_IMPORT_COPY.client.accessSkippedHint}
              </div>
            </div>
          ) : null}

          {plan.skipped.length > 0 ? (
            <div style={card}>
              <div className="font-semibold mb-1">{p.skippedTitle(plan.skipped.length)}</div>
              <ul
                className="flex flex-col gap-1 text-sm"
                style={{
                  paddingLeft: 16,
                  color: "var(--color-text-2)",
                  maxHeight: 280,
                  overflowY: "auto",
                }}
              >
                {plan.skipped
                  .filter((k) => unitFilter !== "incompatible" || k.unit?.kind === "incompatible")
                  .map((k) => {
                    const inc = k.unit?.kind === "incompatible" ? k.unit : null;
                    const m = mappingByRow.get(k.rowId);
                    if (!inc)
                      return (
                        <li key={k.rowId}>
                          <b>{rowLabel.get(k.rowId) ?? k.rowId}</b> — {k.reason}
                        </li>
                      );
                    return (
                      <li key={k.rowId}>
                        <IncompatibleRowItem
                          rowLabel={rowLabel.get(k.rowId) ?? k.rowId}
                          file={inc.file}
                          target={inc.target}
                          onUnitChange={
                            !isClient && props.onMappingChange && m?.targetCode
                              ? () => setUnitDialog(m.targetCode ?? null)
                              : undefined
                          }
                          onSkipRow={
                            !isClient && props.onMappingChange
                              ? () => setSkipConfirm(k.rowId)
                              : undefined
                          }
                        />
                      </li>
                    );
                  })}
              </ul>
            </div>
          ) : null}
        </aside>
      </div>

      {error ? (
        <div role="alert" style={errorBox}>
          {error}
          {issues.length > 0 ? (
            <ul style={{ paddingLeft: 16, marginTop: 4 }}>
              {issues.map((i) => (
                <li key={i}>{i}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button type="button" style={btnGhost} onClick={onBack} disabled={committing}>
          {p.back}
        </button>
        <button
          type="button"
          style={btnPrimary}
          disabled={blocked || committing}
          aria-busy={committing || undefined}
          onClick={() => void commit()}
        >
          {committing ? p.committing : p.commit(toWrite)}
          {unitNote}
        </button>
      </div>
      {/* T15 — AI Kontrolü (mock 2): mevcut set üzerinde deterministik kurallar + LLM checklist; sonuç
          panelde. Aktarım öncesi/sonrası tekrar çalıştırılabilir. */}
      {!isClient ? <AiCheckPanel setId={props.job.setId} canRun compact /> : null}
      <ConfirmDialog
        open={skipConfirm !== null}
        title={KPI_IMPORT_COPY.unit.skipConfirmTitle}
        body={KPI_IMPORT_COPY.unit.skipConfirmBody(
          skipConfirm ? (rowLabel.get(skipConfirm) ?? skipConfirm) : "",
        )}
        confirmLabel={KPI_IMPORT_COPY.unit.skipConfirmAction}
        onConfirm={() => {
          const id = skipConfirm;
          setSkipConfirm(null);
          if (id) skipRow(id);
        }}
        onCancel={() => setSkipConfirm(null)}
      />
      {unitDialog ? (
        <UnitChangeDialog
          setId={job.setId}
          clientId={job.clientId}
          targetCode={unitDialog}
          onApplied={afterUnitApplied}
          onClose={() => setUnitDialog(null)}
        />
      ) : null}
    </div>
  );
}
